// What the public pages read: the airline's live flights and recent totals, and the monthly
// screenshot competition.
//
// The live figures come from the Aviofp backend, which already holds the company's vAMSYS
// client credentials and caches every answer. Nothing here is signed in, and the live flights
// carry no pilot - only the flight and the aeroplane.
import { intelexCsrfToken } from "../intelex/api";

const LIVE_URL = "/aviofp/api/public/live";
const STATS_URL = "/aviofp/api/public/stats";
const BASES_URL = "/aviofp/api/public/bases";
const SCREENSHOTS_URL = "/api/portal/screenshots";

async function readJson(url, { signal } = {}) {
  const response = await fetch(url, { signal, headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`${url} answered ${response.status}`);
  return response.json();
}

export async function fetchLiveFlights({ signal } = {}) {
  const payload = await readJson(LIVE_URL, { signal });
  return {
    available: payload?.available === true,
    count: Number.isFinite(payload?.count) ? payload.count : null,
    flights: Array.isArray(payload?.flights) ? payload.flights : [],
  };
}

export async function fetchBases({ signal } = {}) {
  const payload = await readJson(BASES_URL, { signal });
  return Array.isArray(payload?.bases) ? payload.bases : [];
}

export async function fetchAirlineStats({ days = 30, signal } = {}) {
  const payload = await readJson(`${STATS_URL}?days=${days}`, { signal });
  if (payload?.available !== true) return null;
  return {
    days: payload.days,
    flights: payload.flights ?? null,
    hours: Number.isFinite(payload.flightSeconds) ? Math.round(payload.flightSeconds / 3600) : null,
    passengers: payload.passengers ?? null,
    distanceNm: payload.distanceNm ?? null,
  };
}

export async function fetchScreenshots({ signal } = {}) {
  // `no-cache` revalidates rather than replaying what the browser holds, so a winner published
  // a moment ago shows up on the next load instead of after the cache expires.
  const response = await fetch(SCREENSHOTS_URL, {
    signal,
    cache: "no-cache",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`${SCREENSHOTS_URL} answered ${response.status}`);
  const payload = await response.json();
  return Array.isArray(payload?.entries) ? payload.entries : [];
}

// ---- admin ----

const adminErrors = {
  admin_access_required: "This page needs an administrator account.",
  invalid_period: "Choose the month the competition ran.",
  title_required: "Enter a title.",
  invalid_image: "That file is not a readable JPEG or PNG.",
  unsupported_image_type: "Only JPEG and PNG images can be uploaded.",
  too_many_screenshot_uploads: "Too many uploads for now. Try again later.",
  too_many_screenshots: "The archive is full. Remove an older entry first.",
  screenshot_not_found: "That entry no longer exists.",
  request_too_large: "That image is larger than the 8 MB limit.",
};

const failure = async (response) => {
  const payload = await response.json().catch(() => ({}));
  const error = new Error(
    adminErrors[payload?.error] || `Request failed (${response.status}).`,
  );
  error.code = payload?.error;
  error.status = response.status;
  return error;
};

export async function fetchScreenshotsForAdmin({ signal } = {}) {
  const response = await fetch(`${SCREENSHOTS_URL}/admin/list`, {
    credentials: "include",
    signal,
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw await failure(response);
  const payload = await response.json();
  return Array.isArray(payload?.entries) ? payload.entries : [];
}

// A screenshot straight out of the simulator is often a 2 or 3 MB PNG, and it ends up on the
// home page of a public site. Anything wider than this, or heavier, is redrawn at this width
// and re-encoded as JPEG before it is sent. Nothing is uploaded that the browser could not
// decode, and the original never leaves the admin's machine.
const MAX_WIDTH = 2560;
const REENCODE_OVER_BYTES = 900 * 1024;
const JPEG_QUALITY = 0.86;

async function shrink(file) {
  if (file.size <= REENCODE_OVER_BYTES) return file;
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  try {
    const scale = Math.min(1, MAX_WIDTH / bitmap.width);
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
    // Only worth it if it actually came out smaller.
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } finally {
    bitmap.close?.();
  }
}

export async function uploadScreenshot(original, { period, title, description, credit }) {
  if (!(original instanceof File) || !["image/jpeg", "image/png"].includes(original.type)) {
    throw new Error("Choose a JPEG or PNG image.");
  }
  const file = await shrink(original);
  if (file.size > 8 * 1024 * 1024) throw new Error("Choose an image no larger than 8 MB.");
  const params = new URLSearchParams({ period, title });
  if (description) params.set("description", description);
  if (credit) params.set("credit", credit);
  // The image goes up as its own bytes with its own Content-Type, the way report images do.
  const response = await fetch(`${SCREENSHOTS_URL}?${params}`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": file.type,
      "X-CSRF-Token": intelexCsrfToken(),
    },
    body: file,
  });
  if (!response.ok) throw await failure(response);
  return (await response.json()).entry;
}

export async function updateScreenshot(id, fields) {
  const response = await fetch(`${SCREENSHOTS_URL}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": intelexCsrfToken(),
    },
    body: JSON.stringify(fields),
  });
  if (!response.ok) throw await failure(response);
  return (await response.json()).entry;
}

export async function deleteScreenshot(id) {
  const response = await fetch(`${SCREENSHOTS_URL}/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json", "X-CSRF-Token": intelexCsrfToken() },
  });
  if (!response.ok) throw await failure(response);
}

// ---- the fleet page's content ----

const FLEET_URL = "/api/portal/fleet";

const fleetErrors = {
  invalid_fleet_content: "That content could not be saved.",
  fleet_needs_a_section: "Keep at least one section.",
  section_title_required: "Every section needs a title.",
  card_title_required: "Every card needs a title.",
  too_many_fleet_sections: "That is more sections than the page can hold.",
  too_many_fleet_cards: "That is more cards than a section can hold.",
  too_many_fleet_uploads: "Too many uploads for now. Try again later.",
};

const fleetFailure = async (response) => {
  const payload = await response.json().catch(() => ({}));
  const error = new Error(
    fleetErrors[payload?.error] || adminErrors[payload?.error] || `Request failed (${response.status}).`,
  );
  error.code = payload?.error;
  error.status = response.status;
  return error;
};

// Whether the fleet page is on, and its words when it is. `content` is null when nothing has
// been saved, which means the page should use the copy it ships with.
//
// Asked for afresh each time rather than held: it is one small revalidated request, and a held
// copy would go stale the moment the page was switched on or off from the editor.
export async function fetchFleetState({ signal } = {}) {
  const response = await fetch(FLEET_URL, {
    signal,
    cache: "no-cache",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`${FLEET_URL} answered ${response.status}`);
  const payload = await response.json();
  return { enabled: payload?.enabled === true, content: payload?.content ?? null };
}

export async function fetchFleetForAdmin({ signal } = {}) {
  const response = await fetch(`${FLEET_URL}/admin/document`, {
    credentials: "include",
    signal,
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw await fleetFailure(response);
  return response.json();
}

export async function saveFleetContent(content, enabled) {
  const response = await fetch(FLEET_URL, {
    method: "PUT",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": intelexCsrfToken(),
    },
    body: JSON.stringify({ content, enabled: enabled === true }),
  });
  if (!response.ok) throw await fleetFailure(response);
  return response.json();
}

export async function uploadFleetImage(original) {
  if (!(original instanceof File) || !["image/jpeg", "image/png"].includes(original.type)) {
    throw new Error("Choose a JPEG or PNG image.");
  }
  const file = await shrink(original);
  if (file.size > 8 * 1024 * 1024) throw new Error("Choose an image no larger than 8 MB.");
  const response = await fetch(`${FLEET_URL}/images`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": file.type,
      "X-CSRF-Token": intelexCsrfToken(),
    },
    body: file,
  });
  if (!response.ok) throw await fleetFailure(response);
  return (await response.json()).image;
}

// ---- the partner logos along the foot of the home page ----

const PARTNERS_URL = "/api/portal/partners";

const partnerErrors = {
  invalid_partners: "That list could not be saved.",
  partner_logo_required: "Every partner needs a logo.",
  partner_name_required: "Every partner needs a name.",
  too_many_partners: "That is more partners than the band can hold.",
  too_many_partner_uploads: "Too many uploads for now. Try again later.",
};

const partnerFailure = async (response) => {
  const payload = await response.json().catch(() => ({}));
  const error = new Error(
    partnerErrors[payload?.error] || adminErrors[payload?.error] || `Request failed (${response.status}).`,
  );
  error.code = payload?.error;
  error.status = response.status;
  return error;
};

// null when nothing has been saved, which means the page should use the list it ships with.
export async function fetchPartners({ signal } = {}) {
  const response = await fetch(PARTNERS_URL, {
    signal,
    cache: "no-cache",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`${PARTNERS_URL} answered ${response.status}`);
  const payload = await response.json();
  return Array.isArray(payload?.partners) ? payload.partners : null;
}

export async function fetchPartnersForAdmin({ signal } = {}) {
  const response = await fetch(`${PARTNERS_URL}/admin/document`, {
    credentials: "include",
    signal,
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw await partnerFailure(response);
  return response.json();
}

export async function savePartners(partners) {
  const response = await fetch(PARTNERS_URL, {
    method: "PUT",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": intelexCsrfToken(),
    },
    body: JSON.stringify({ partners }),
  });
  if (!response.ok) throw await partnerFailure(response);
  return response.json();
}

// The band draws every badge in a box of the same size, which is only even if each badge
// fills its own file. A PNG exported with a wide transparent margin draws its artwork smaller
// than the one beside it, so the margin is cropped off before the file is sent: the image goes
// onto a canvas, the alpha channel gives the box that holds the artwork, and that box is what
// is uploaded. The same crop the shipped logos were given by scripts/trim-partner-logos.py.
//
// A JPEG has no alpha and is returned untouched, as is anything the browser will not let us
// read back (it should not happen for a local file, but a tainted canvas throws).
const ALPHA_FLOOR = 8;

async function trimmedLogo(file) {
  if (file.type !== "image/png") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    context.drawImage(bitmap, 0, 0);
    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);

    let top = canvas.height;
    let left = canvas.width;
    let right = -1;
    let bottom = -1;
    for (let y = 0; y < canvas.height; y += 1) {
      for (let x = 0; x < canvas.width; x += 1) {
        if (data[(y * canvas.width + x) * 4 + 3] <= ALPHA_FLOOR) continue;
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
    // Nothing visible at all, or nothing to crop.
    if (right < left || bottom < top) return file;
    const width = right - left + 1;
    const height = bottom - top + 1;
    if (width === canvas.width && height === canvas.height) return file;

    const cropped = document.createElement("canvas");
    cropped.width = width;
    cropped.height = height;
    cropped.getContext("2d").drawImage(canvas, left, top, width, height, 0, 0, width, height);
    const blob = await new Promise((resolve) => cropped.toBlob(resolve, "image/png"));
    return blob ? new File([blob], file.name, { type: "image/png" }) : file;
  } catch {
    // Whatever went wrong, the logo the admin chose is still a logo.
    return file;
  }
}

export async function uploadPartnerImage(original) {
  if (!(original instanceof File) || !["image/jpeg", "image/png"].includes(original.type)) {
    throw new Error("Choose a JPEG or PNG image.");
  }
  const file = await trimmedLogo(original);
  if (file.size > 4 * 1024 * 1024) throw new Error("Choose an image no larger than 4 MB.");
  const response = await fetch(`${PARTNERS_URL}/images`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": file.type,
      "X-CSRF-Token": intelexCsrfToken(),
    },
    body: file,
  });
  if (!response.ok) throw await partnerFailure(response);
  return (await response.json()).image;
}

// ---- shared formatting ----

// "2026-10" as "October 2026". Built from the parts rather than parsed as a date, so it cannot
// slip a month at either end of a time zone.
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function monthName(period) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(period ?? ""));
  if (!match) return String(period ?? "");
  const month = MONTHS[Number(match[2]) - 1];
  return month ? `${month} ${match[1]}` : match[1];
}
